import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Library, Loader2, Trash2, Upload } from 'lucide-react'
import {
  MODEL_FILE_EXTENSIONS,
  MODEL_PRESETS,
  inferModelAssetType,
} from '@/lib/modelPresets.js'
import {
  uploadBuildingAssetFile,
  uploadFloorAssetFile,
  uploadSiteAssetFile,
} from '@/lib/api/spaces.js'
import { useRoomScans } from '@/features/assets/useRoomScans.js'

const SCOPE_CONFIG = {
  site: {
    label: 'Site 3D model (optional exterior)',
    saveFirstHint: 'Save the site first to upload a 3D model.',
    upload: uploadSiteAssetFile,
    helper:
      'Optional exterior/context model. Use the per-building model for interior wayfinding.',
  },
  building: {
    label: 'Building 3D model',
    saveFirstHint: 'Save the building first to upload a 3D model.',
    upload: uploadBuildingAssetFile,
    helper:
      'Single model that covers all floors of this building. Floor-level overrides remain available in the floor editor.',
  },
  floor: {
    label: 'Floor 3D model (override)',
    saveFirstHint: 'Save the floor first to upload a 3D model.',
    upload: uploadFloorAssetFile,
    helper:
      'Overrides the building model for this floor only. Leave empty to inherit from the building or site.',
  },
}

const EMPTY_FORM = {
  preset: 'none',
  local_url: '',
  file_name: '',
  asset_type: 'fbx',
  bucket: '',
  storage_key: '',
  scale: '',
  north_rotation: '',
  origin_x: '',
  origin_y: '',
  elevation: '',
}

export default function ModelFormSection({ scope, entityId, form, onChange }) {
  const config = SCOPE_CONFIG[scope]
  if (!config) throw new Error(`ModelFormSection: unknown scope "${scope}"`)

  const fileInputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [showLibrary, setShowLibrary] = useState(false)
  const { data: roomScans = [], isLoading: scansLoading } = useRoomScans()

  useEffect(() => {
    setUploadError(null)
  }, [entityId, scope])

  const selectedRoomScan = useMemo(() => {
    if (!form.storage_key) return null
    return roomScans.find((scan) => scan.storage_key === form.storage_key) || null
  }, [roomScans, form.storage_key])

  const setPreset = (presetId) => {
    const preset = MODEL_PRESETS.find((p) => p.id === presetId)
    if (!preset) return
    if (presetId === 'none') {
      onChange({ ...EMPTY_FORM })
      return
    }
    if (presetId === 'custom') {
      onChange((cur) => ({ ...cur, preset: 'custom', bucket: '', storage_key: '' }))
      return
    }
    const p = preset.payload
    onChange({
      ...EMPTY_FORM,
      preset: presetId,
      local_url: p.local_url,
      file_name: p.file_name || '',
      asset_type: p.asset_type || 'fbx',
      scale: p.transform?.scale !== undefined && p.transform.scale !== 1 ? String(p.transform.scale) : '',
      north_rotation: p.transform?.north_rotation ? String(p.transform.north_rotation) : '',
      origin_x: p.transform?.origin_x ? String(p.transform.origin_x) : '',
      origin_y: p.transform?.origin_y ? String(p.transform.origin_y) : '',
      elevation: p.transform?.elevation ? String(p.transform.elevation) : '',
    })
  }

  const update = (field, value) => onChange((cur) => ({ ...cur, [field]: value }))

  const pickRoomScan = (scan) => {
    if (!scan) return
    const fileName = scan.name?.toLowerCase().endsWith('.usdz')
      ? scan.name
      : `${scan.name || 'room-scan'}.usdz`
    onChange({
      ...EMPTY_FORM,
      preset: 'library',
      local_url: scan.download_url || '',
      file_name: fileName,
      asset_type: 'usdz',
      bucket: scan.bucket || '',
      storage_key: scan.storage_key || '',
    })
    setShowLibrary(false)
    setUploadError(null)
  }

  const handlePickFile = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (!entityId) {
      setUploadError(config.saveFirstHint)
      return
    }
    const lower = file.name.toLowerCase()
    if (!MODEL_FILE_EXTENSIONS.some((ext) => lower.endsWith(ext))) {
      setUploadError('Choose an FBX, GLB, glTF, or USDZ model file.')
      return
    }
    setUploadError(null)
    setUploading(true)
    try {
      const result = await config.upload(entityId, file)
      onChange((cur) => ({
        ...cur,
        preset: 'custom',
        local_url: result.local_url,
        file_name: result.file_name || file.name,
        asset_type: result.asset_type || inferModelAssetType(file.name),
        bucket: '',
        storage_key: '',
      }))
    } catch (err) {
      setUploadError(err?.uiMessage || err?.message || 'Upload failed.')
    } finally {
      setUploading(false)
    }
  }

  const clearSelection = () => {
    onChange({ ...EMPTY_FORM })
    setUploadError(null)
  }

  const hasSelection = Boolean(form.local_url) || Boolean(form.storage_key)
  const isUploadedFile = form.local_url?.startsWith('/site-uploads/')
  const isLibraryAsset = Boolean(form.storage_key)
  const isPresetFile =
    !isUploadedFile && !isLibraryAsset && form.preset && form.preset !== 'none' && form.preset !== 'custom'

  return (
    <>
      <div className="auth-field">
        <span className="auth-field__label">{config.label}</span>
        <div className="col gap-8">
          <input
            ref={fileInputRef}
            type="file"
            accept={MODEL_FILE_EXTENSIONS.join(',')}
            onChange={handlePickFile}
            style={{ display: 'none' }}
          />

          {hasSelection ? (
            <div
              className="row"
              style={{
                gap: 8,
                padding: '8px 10px',
                border: '1px solid var(--border)',
                borderRadius: 8,
                background: 'var(--surface-sunken)',
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {form.file_name || selectedRoomScan?.name || form.local_url}
                </div>
                <div className="muted" style={{ fontSize: 11, wordBreak: 'break-all' }}>
                  {isLibraryAsset
                    ? `From library${selectedRoomScan ? `: ${selectedRoomScan.name}` : ''}`
                    : isUploadedFile
                    ? 'Uploaded'
                    : isPresetFile
                    ? 'Preset model'
                    : 'Custom URL'}{' · '}
                  {form.asset_type?.toUpperCase() || 'FBX'}
                </div>
              </div>
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => setShowLibrary((v) => !v)}
                title="Pick another model from your library"
              >
                <Library size={12} /> Library
              </button>
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || !entityId}
                title={!entityId ? config.saveFirstHint : 'Replace file'}
              >
                {uploading ? <><Loader2 size={12} className="spin" /> Uploading…</> : <><Upload size={12} /> Replace</>}
              </button>
              <button
                type="button"
                className="btn btn--sm"
                onClick={clearSelection}
                disabled={uploading}
                aria-label="Remove selected model"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ) : (
            <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => setShowLibrary((v) => !v)}
                title="Pick a model from your asset library"
              >
                <Library size={12} /> Pick from assets
              </button>
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || !entityId}
                title={!entityId ? config.saveFirstHint : 'Choose a 3D model file'}
              >
                {uploading ? <><Loader2 size={12} className="spin" /> Uploading…</> : <><Upload size={12} /> Choose file</>}
              </button>
              <span className="muted" style={{ fontSize: 11 }}>
                {entityId ? 'FBX, GLB, glTF, or USDZ up to 250 MB.' : config.saveFirstHint}
              </span>
            </div>
          )}

          {showLibrary && (
            <RoomScanLibrary
              scans={roomScans}
              loading={scansLoading}
              selectedKey={form.storage_key}
              onPick={pickRoomScan}
              onClose={() => setShowLibrary(false)}
            />
          )}

          <span className="muted" style={{ fontSize: 11 }}>{config.helper}</span>

          {uploadError && (
            <div className="auth-error" role="alert">
              <span>{uploadError}</span>
            </div>
          )}

          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setShowAdvanced((v) => !v)}
            style={{ alignSelf: 'flex-start', padding: 0 }}
          >
            {showAdvanced ? 'Hide advanced options' : 'Use a preset or URL instead'}
          </button>
        </div>
      </div>

      {showAdvanced && (
        <>
          <label className="auth-field">
            <span className="auth-field__label">Preset</span>
            <select value={form.preset} onChange={(e) => setPreset(e.target.value)}>
              {MODEL_PRESETS.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
          </label>

          {form.preset !== 'none' && (
            <>
              <label className="auth-field">
                <span className="auth-field__label">URL or path</span>
                <input
                  value={form.local_url}
                  onChange={(e) => {
                    const v = e.target.value
                    onChange((cur) => ({ ...cur, local_url: v, preset: 'custom' }))
                  }}
                  placeholder="/fixtures/newport-airbnb.fbx or https://…"
                />
              </label>
              <label className="auth-field">
                <span className="auth-field__label">Filename</span>
                <input
                  value={form.file_name}
                  onChange={(e) => update('file_name', e.target.value)}
                  placeholder="auto"
                />
              </label>
              <label className="auth-field">
                <span className="auth-field__label">Type</span>
                <select value={form.asset_type} onChange={(e) => update('asset_type', e.target.value)}>
                  <option value="fbx">FBX</option>
                  <option value="glb">GLB / glTF</option>
                  <option value="usdz">USDZ / USD</option>
                </select>
              </label>

              <div className="inspector__group-title" style={{ marginTop: 4 }}>Model transform</div>
              <div className="row" style={{ alignItems: 'flex-start', gap: 8 }}>
                <label className="auth-field" style={{ flex: 1 }}>
                  <span className="auth-field__label">Scale</span>
                  <input
                    type="number"
                    step="0.001"
                    value={form.scale}
                    onChange={(e) => update('scale', e.target.value)}
                    placeholder="1"
                  />
                </label>
                <label className="auth-field" style={{ flex: 1 }}>
                  <span className="auth-field__label">North rotation°</span>
                  <input
                    type="number"
                    step="1"
                    value={form.north_rotation}
                    onChange={(e) => update('north_rotation', e.target.value)}
                    placeholder="0"
                  />
                </label>
              </div>
              <div className="row" style={{ alignItems: 'flex-start', gap: 8 }}>
                <label className="auth-field" style={{ flex: 1 }}>
                  <span className="auth-field__label">Origin X</span>
                  <input
                    type="number"
                    step="0.1"
                    value={form.origin_x}
                    onChange={(e) => update('origin_x', e.target.value)}
                    placeholder="0"
                  />
                </label>
                <label className="auth-field" style={{ flex: 1 }}>
                  <span className="auth-field__label">Origin Y</span>
                  <input
                    type="number"
                    step="0.1"
                    value={form.origin_y}
                    onChange={(e) => update('origin_y', e.target.value)}
                    placeholder="0"
                  />
                </label>
                <label className="auth-field" style={{ flex: 1 }}>
                  <span className="auth-field__label">Elevation</span>
                  <input
                    type="number"
                    step="0.1"
                    value={form.elevation}
                    onChange={(e) => update('elevation', e.target.value)}
                    placeholder="0"
                  />
                </label>
              </div>
              <span className="muted" style={{ fontSize: 11 }}>
                Leave transform fields blank to use defaults (scale 1, origins 0).
              </span>
            </>
          )}
        </>
      )}
    </>
  )
}

function RoomScanLibrary({ scans, loading, selectedKey, onPick, onClose }) {
  return (
    <div
      className="col gap-8"
      style={{
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: 8,
        background: 'var(--surface)',
      }}
    >
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <strong style={{ fontSize: 13 }}>Your room scans</strong>
        <button type="button" className="btn btn--sm btn--ghost" onClick={onClose}>
          Cancel
        </button>
      </div>
      {loading ? (
        <div className="muted" style={{ fontSize: 12 }}>Loading scans…</div>
      ) : scans.length === 0 ? (
        <div className="muted" style={{ fontSize: 12 }}>
          No room scans yet. Capture one with the ARrow Scanner iOS app.
        </div>
      ) : (
        <div
          className="col gap-4"
          style={{ maxHeight: 240, overflowY: 'auto' }}
        >
          {scans.map((scan) => {
            const isSelected = scan.storage_key === selectedKey
            return (
              <button
                key={scan.id}
                type="button"
                onClick={() => onPick(scan)}
                className="row"
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 10px',
                  gap: 10,
                  alignItems: 'center',
                  border: `1px solid ${isSelected ? 'var(--accent-strong)' : 'var(--border)'}`,
                  borderRadius: 6,
                  background: isSelected ? 'var(--accent-soft)' : 'transparent',
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 6,
                    background: 'var(--surface-sunken)',
                    display: 'grid',
                    placeItems: 'center',
                    overflow: 'hidden',
                    flexShrink: 0,
                  }}
                  aria-hidden
                >
                  {scan.thumbnail_url || scan.floorplan_png_url ? (
                    <img
                      src={scan.thumbnail_url || scan.floorplan_png_url}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Box size={16} />
                  )}
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {scan.name}
                  </div>
                  <div className="muted" style={{ fontSize: 11 }}>
                    {scan.dimensions_meters?.length && scan.dimensions_meters?.width
                      ? `${Number(scan.dimensions_meters.length).toFixed(1)} × ${Number(scan.dimensions_meters.width).toFixed(1)} m · `
                      : ''}
                    USDZ
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
